import { useState } from 'react'
import { NavLink } from 'react-router'
import { toast } from 'react-toastify'
import { FiArrowRight } from 'react-icons/fi'
import {
  FaLinkedin,
  FaInstagram,
  FaTiktok,
  FaXTwitter,
  FaFacebook,
  FaYoutube,
} from 'react-icons/fa6'
import { Container } from '@/components/common'
import { ROUTES } from '@/constants/routes'
import { details } from '@/constants/contact'
import { newsletterSchema } from '@/utils'
import { sendNewsletterSignup } from '@/lib/emailjs'
import { cn } from '@/lib/cn'
import logoDark from '@/assets/logo/all blueblack.png'

const quickLinks = [
  { to: ROUTES.HOME, label: 'Home' },
  { to: ROUTES.ABOUT, label: 'About Us' },
  { to: ROUTES.PROGRAMMES, label: 'Programmes' },
  { to: ROUTES.IMPACT, label: 'Our Impact' },
  { to: ROUTES.MAGAZINE, label: 'Magazine' },
  { to: ROUTES.TEAM, label: 'Our Team' },
  { to: ROUTES.GALLERY, label: 'Gallery' },
  { to: ROUTES.CONTACT, label: 'Contact Us' },
]

const programmes = [
  { to: ROUTES.PROGRAMMES, label: 'AfCa-YEIH' },
  { to: ROUTES.PROGRAMMES, label: 'TUWE' },
  { to: ROUTES.PROGRAMMES, label: 'AfCaSPIH Nexus' },
  { to: ROUTES.PROGRAMMES, label: 'AfCa-CF' },
]

const resources = [
  { to: ROUTES.MAGAZINE, label: 'IBON World Magazine' },
  { to: ROUTES.IMPACT, label: 'Impact Reports' },
  { to: ROUTES.GALLERY, label: 'Photo Gallery' },
  { to: ROUTES.IMPACT, label: 'News & Updates' },
  { to: ROUTES.IMPACT, label: 'Events & Opportunities' },
]

const socials = [
  { Icon: FaLinkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/ibonworld/' },
  { Icon: FaInstagram, label: 'Instagram', href: 'https://www.instagram.com/ibonworld/' },
  { Icon: FaXTwitter, label: 'X (Twitter)', href: 'https://x.com/ibon_world' },
  { Icon: FaFacebook, label: 'Facebook', href: 'https://www.facebook.com/Ibonworld' },
  { Icon: FaYoutube, label: 'YouTube', href: 'https://www.youtube.com/@ibonworld' },
  { Icon: FaTiktok, label: 'TikTok', href: 'https://www.tiktok.com/@ibonworld' },
]

export const Footer = () => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()

    const result = newsletterSchema.safeParse({ email })
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? 'Enter a valid email address')
      return
    }

    setError('')
    setSending(true)

    try {
      await sendNewsletterSignup(result.data.email)
      toast.success("Subscribed! We'll be in touch.")
      setSubmitted(true)
      setEmail('')
      setTimeout(() => setSubmitted(false), 4000)
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <footer className="border-t border-border bg-white text-ibon-dark">
      {/* Brand colour accent line */}
      <div aria-hidden className="flex h-[3px] w-full">
        <span className="flex-1 bg-ibon-sky" />
        <span className="flex-1 bg-ibon-azure" />
        <span className="flex-1 bg-ibon-steel" />
        <span className="flex-1 bg-ibon-navy" />
        <span className="flex-1 bg-ibon-dark" />
        <span className="flex-1 bg-ibon-midnight" />
      </div>

      {/* Newsletter strip */}
      <div id="newsletter" className="scroll-mt-24 border-b border-border bg-surface/60">
        <Container className="flex flex-col gap-6 py-12 sm:flex-row sm:items-center sm:justify-between">
          <div className="shrink-0">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ibon-sky">
              Stay Connected
            </p>
            <h3 className="mt-2 text-2xl font-bold tracking-tight text-ibon-dark">
              Get the latest from IBON World
            </h3>
          </div>
          <div className="w-full max-w-md">
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (error) setError('')
                }}
                className={cn(
                  'min-w-0 flex-1 rounded-full border border-border bg-white px-5 py-3 text-sm text-ibon-dark placeholder:text-text-muted focus:border-ibon-sky focus:outline-none',
                  error && 'border-red-500 focus:border-red-500',
                )}
              />
              <button
                type="submit"
                disabled={sending}
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-ibon-sky px-6 py-3 text-xs font-semibold uppercase tracking-wide text-ibon-dark transition-colors hover:bg-[#6fcdef] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? 'Sending...' : submitted ? 'Subscribed!' : 'Subscribe'}
                {!sending && !submitted && (
                  <FiArrowRight
                    className="transition-transform duration-300 group-hover:translate-x-1"
                    size={14}
                  />
                )}
              </button>
            </form>
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          </div>
        </Container>
      </div>

      {/* Main columns */}
      <Container className="grid gap-12 py-16 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr]">
        {/* Brand */}
        <div>
          <NavLink to={ROUTES.HOME} aria-label="IBON World home">
            <img src={logoDark} alt="IBON World" className="h-10 w-auto" />
          </NavLink>
          <p className="mt-5 text-sm font-medium leading-relaxed text-ibon-dark">
            Connecting Entrepreneurship, Education, and Culture Across Africa and the Caribbean.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-text-muted">
            IBON World is an international non-profit organisation working to create opportunities
            and strengthen communities through entrepreneurship, education, cultural exchange, and
            humanitarian advocacy.
          </p>

          <ul className="mt-6 flex flex-col gap-3">
            {details.map(({ Icon, label }) => (
              <li key={label} className="flex items-start gap-3 text-sm text-text-muted">
                <Icon className="mt-0.5 shrink-0 text-ibon-sky" size={16} />
                <span>{label}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            {socials.map(({ Icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-text-muted transition-all duration-300 hover:-translate-y-0.5 hover:border-ibon-sky hover:text-ibon-sky hover:shadow-sm"
              >
                <Icon size={14} />
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark">
            Quick Links
          </h4>
          <ul className="mt-6 flex flex-col gap-3">
            {quickLinks.map((link) => (
              <li key={link.to + link.label}>
                <NavLink
                  to={link.to}
                  end={link.to === ROUTES.HOME}
                  className="text-sm text-text-muted transition-colors hover:text-ibon-sky"
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Programmes */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark">
            Programmes
          </h4>
          <ul className="mt-6 flex flex-col gap-3">
            {programmes.map((p) => (
              <li key={p.label}>
                <NavLink
                  to={p.to}
                  className="text-sm text-text-muted transition-colors hover:text-ibon-sky"
                >
                  {p.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark">
            Resources
          </h4>
          <ul className="mt-6 flex flex-col gap-3">
            {resources.map((r) => (
              <li key={r.label}>
                <NavLink
                  to={r.to}
                  className="text-sm text-text-muted transition-colors hover:text-ibon-sky"
                >
                  {r.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </Container>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <Container className="grid gap-3 py-6 text-center sm:grid-cols-3 sm:text-left">
          <p className="text-xs text-text-muted">
            © {new Date().getFullYear()} IBON World. All Rights Reserved.
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-text-muted">
            <NavLink to={ROUTES.PRIVACY} className="transition-colors hover:text-ibon-dark">
              Privacy Policy
            </NavLink>
            <span className="text-border">|</span>
            <NavLink to={ROUTES.TERMS} className="transition-colors hover:text-ibon-dark">
              Terms of Use
            </NavLink>
            <span className="text-border">|</span>
            <NavLink
              to={`${ROUTES.PRIVACY}#cookies`}
              className="transition-colors hover:text-ibon-dark"
            >
              Cookie Policy
            </NavLink>
          </nav>
          <p className="text-xs text-text-muted sm:text-right">
            Designed and Developed by ICS Africa Ltd
          </p>
        </Container>
      </div>
    </footer>
  )
}
